import { Stack, Text, TextInput } from '@mantine/core'
import { IconMapPin, IconSearch } from '@tabler/icons-react'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getFindMyTutorCmsCountries } from 'src/apis/findMyTutorAPI'
import TableComponent from 'src/components/TableComponent'
import FindMyTutorPanelHeader from './FindMyTutorPanelHeader'
import styles from './TutorPage.module.scss'

interface TutorCountryRow {
	no: number
	country: string
	amount: number
}

const getRowsAndCount = (response: any) => {
	const objects = response?.data?.results?.objects
	const rows = Array.isArray(objects?.rows) ? objects.rows : []

	return {
		rows,
		count: Number(
			objects?.count ?? response?.data?.pagination?.total ?? rows.length,
		),
	}
}

function TutorCountryPage() {
	const [, setSearchParams] = useSearchParams()
	const [rows, setRows] = useState<TutorCountryRow[]>([])
	const [searchKey, setSearchKey] = useState('')
	const [loading, setLoading] = useState(false)
	const [total, setTotal] = useState(0)
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 1,
		pageSize: 10,
	})

	const fetchCountries = useCallback(async () => {
		setLoading(true)

		try {
			const response = await getFindMyTutorCmsCountries({
				page: pagination.pageIndex,
				limit: pagination.pageSize,
				q: searchKey || undefined,
			})
			const { rows: apiRows, count } = getRowsAndCount(response)

			setRows(
				apiRows.map((item: any, index: number) => ({
					no:
						index +
						1 +
						pagination.pageSize * (pagination.pageIndex - 1),
					country: item?.country || '',
					amount: Number(item?.amount || 0),
				})),
			)
			setTotal(count)
		} finally {
			setLoading(false)
		}
	}, [pagination, searchKey])

	useEffect(() => {
		fetchCountries()
	}, [fetchCountries])

	const handleCountryClick = useCallback(
		(country: string) => {
			setSearchParams((previous) => {
				const next = new URLSearchParams(previous)
				next.set('tab', 'tutor')
				next.set('country', country)
				next.set('page', '1')
				next.delete('country_search')
				next.delete('search')
				next.delete('search_key')
				return next
			}, { replace: true })
		},
		[setSearchParams],
	)

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'no',
				header: 'STT',
				size: 70,
			},
			{
				accessorKey: 'country',
				header: 'Country',
				Cell: ({ cell }: any) => {
					const country = String(cell.getValue() || '')

					return (
						<button
							type="button"
							className={styles.countryLink}
							onClick={() => handleCountryClick(country)}
						>
							<IconMapPin size={15} />
							{country}
						</button>
					)
				},
			},
			{
				accessorKey: 'amount',
				header: 'Amount of tutors',
				size: 180,
			},
		],
		[handleCountryClick],
	)

	return (
		<Stack className={styles.page} spacing={16}>
			<FindMyTutorPanelHeader
				title="Tutor Countries"
				description="View tutor totals by country and open the filtered tutor list."
				controls={
					<Stack spacing={6} className={styles.searchControl}>
						<Text size="sm" fw={700} color="#344054">
							Search country
						</Text>
						<TextInput
							value={searchKey}
							onChange={(event) => {
								setSearchKey(event.currentTarget.value)
								setPagination((previous) => ({
									...previous,
									pageIndex: 1,
								}))
							}}
							placeholder="Search country"
							icon={<IconSearch size={16} />}
						/>
					</Stack>
				}
			/>

			<div className={styles.tableWrap}>
				<TableComponent
					columns={columns}
					total={total}
					data={rows}
					pagination={pagination}
					loading={loading}
					handleChangePage={(pageIndex: number) =>
						setPagination((previous) => ({ ...previous, pageIndex }))
					}
					handleChangePageSize={(pageSize: number) =>
						setPagination({ pageIndex: 1, pageSize })
					}
					searchQueryKey="country_search"
					searchQueryValue={searchKey}
					onSearchQueryInit={setSearchKey}
					enableRowActions={false}
					compact
				/>
			</div>
		</Stack>
	)
}

export default TutorCountryPage
